import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/shared/lib/toast';
import {
  remindersService,
  type Reminder,
  type CreateReminderData,
  type UpdateReminderData,
} from '../services/reminders.service';

/**
 * Tipo para erros da API
 */
interface ApiError {
  response?: {
    data?: {
      error?: string;
    };
  };
  message?: string;
}

/**
 * Helper para extrair mensagem de erro
 */
function getErrorMessage(error: unknown, defaultMessage: string): string {
  const apiError = error as ApiError;
  return apiError?.response?.data?.error || apiError?.message || defaultMessage;
}

/**
 * Hook para listar lembretes
 */
export function useReminders(status?: Reminder['status']) {
  return useQuery({
    queryKey: ['reminders', status],
    queryFn: () => remindersService.listReminders(status),
  });
}

/**
 * Hook para buscar estatísticas de lembretes
 */
export function useReminderStats() {
  return useQuery({
    queryKey: ['reminders', 'stats'],
    queryFn: () => remindersService.getStats(),
  });
}

/**
 * Hook para buscar um lembrete específico
 */
export function useReminder(id: string) {
  return useQuery({
    queryKey: ['reminders', id],
    queryFn: () => remindersService.getReminder(id),
    enabled: !!id,
  });
}

/**
 * Hook para criar lembrete
 */
export function useCreateReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReminderData) => remindersService.createReminder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      toast.success('Lembrete criado com sucesso!');
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao criar lembrete');
      toast.error(message);
    },
  });
}

/**
 * Hook para atualizar lembrete
 */
export function useUpdateReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReminderData }) =>
      remindersService.updateReminder(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      queryClient.invalidateQueries({ queryKey: ['reminders', variables.id] });
      toast.success('Lembrete atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao atualizar lembrete');
      toast.error(message);
    },
  });
}

/**
 * Hook para cancelar lembrete
 */
export function useCancelReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => remindersService.cancelReminder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      toast.success('Lembrete cancelado!');
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao cancelar lembrete');
      toast.error(message);
    },
  });
}

/**
 * Hook para deletar lembrete
 */
export function useDeleteReminder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => remindersService.deleteReminder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      toast.success('Lembrete deletado!');
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao deletar lembrete');
      toast.error(message);
    },
  });
}
